import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback, useState } from "react";

import InputVariableConfigurator from "@/components/InputVariableConfigurator";
import { Variable } from "@/components/InputVariableConfigurator/types";
import { usePlayground } from "@/components/Playground/playground-store";
import {
  PLAYGROUND_DRAWER_MODES,
  TemplateFormat,
  VariableSet,
} from "@/components/Playground/types";
import { getVariableValueIsFilled } from "@/components/Playground/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { useAPIResetChatFromRequest } from "@/queries/playground/useAPIResetChatFromRequest";
import { RequestToolData } from "@/queries/requests/useRequestLogInputVariables";
import { getMissingToolData } from "@/utils/playground";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface EditTestCaseDialogProps {
  set?: VariableSet;
  isOpen: boolean;
  onClose: () => void;
}

interface ImportedThreadMessage {
  role?: unknown;
  content?: unknown;
}

const parseImportedThreadMessages = (value: string): ImportedThreadMessage[] | null => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return null;
    }

    return parsed.filter(
      (message): message is ImportedThreadMessage =>
        typeof message === "object" && message !== null,
    );
  } catch {
    return null;
  }
};

const normalizeContentForComparison = (content: unknown): unknown => {
  if (typeof content === "string") {
    return [{ type: "text", text: content }];
  }

  return content ?? [];
};

const contentMatches = (left: unknown, right: unknown) =>
  JSON.stringify(normalizeContentForComparison(left)) ===
  JSON.stringify(normalizeContentForComparison(right));

const EditTestCaseDialog = observer(({ set, isOpen, onClose }: EditTestCaseDialogProps) => {
  const playground = usePlayground();
  const { userToken } = useAuth() ?? {};
  const [dialogContentElement, setDialogContentElement] = useState<HTMLDivElement | null>(null);

  const resetChatMutation = useAPIResetChatFromRequest(userToken ?? "", {
    onSuccess: (data, variables) => {
      // Signal the hydration hook to re-fetch from the server.
      // requestRehydration clears the local messages AND bumps the
      // generation counter so useHydrateChatMessages resets its
      // hasInitialized ref and re-processes the fresh query data.
      if (set) {
        const variableSetIndex = playground.input_variable_sets.findIndex(s => s.id === set.id);
        if (variableSetIndex >= 0) {
          playground.chatStore.requestRehydration(variableSetIndex);
        }
      }

      // Show a consolidated toast based on what was imported.
      const hasVariables = Object.keys(variables.request.input_variables).length > 0;
      const hasChatHistory = data.has_initial_chat_history;

      if (hasVariables && hasChatHistory) {
        displayToast("Variables imported. Chat history applied.", ToastType.success);
      } else if (hasVariables) {
        displayToast("Variables imported successfully!", ToastType.success);
      } else if (hasChatHistory) {
        displayToast("Chat history imported from request.", ToastType.success);
      } else {
        displayErrorToast("No input variables or chat history found for this request");
      }
    },
    onError: (error: Error) => {
      displayErrorToast(error.message || "Failed to reset chat from request");
    },
  });

  const handleVariablesChange = (variables: Variable[]) => {
    if (!set) {
      return;
    }

    // Clear existing variables for this set
    playground.clearInputVariableSet(set.id);

    // Add updated variables
    variables.forEach(variable => {
      playground.updateInputVariable(set.id)(variable.id, variable.key, variable.value);
    });
  };

  const variableSets = playground.input_variable_sets;
  const activeSetIndex = set
    ? variableSets.findIndex(variableSet => variableSet.id === set.id)
    : -1;
  const detectedVariableCount = playground.detectedInputVariables.length;
  const filledVariableCount =
    set?.variables.filter(variable => getVariableValueIsFilled(variable.value)).length ?? 0;
  const isEvaluateMode = playground.drawerMode === PLAYGROUND_DRAWER_MODES.EVALUATE;
  const setStatusText = isEvaluateMode
    ? `${filledVariableCount} ${filledVariableCount === 1 ? "override" : "overrides"}`
    : `${filledVariableCount}/${detectedVariableCount} filled`;
  const setName = set?.name || "Untitled set";
  const canNavigateSets = variableSets.length > 1 && activeSetIndex >= 0;
  const templateFormatOptions: TemplateFormat[] = ["jinja2", "f-string"];

  const navigateToSet = (setId: string) => {
    playground.setActiveInputVariableSetId(setId);
    playground.startEditingVariableSet(setId);
  };

  const navigateByOffset = (offset: number) => {
    if (!canNavigateSets) {
      return;
    }

    const nextIndex = activeSetIndex + offset;
    const nextSet = variableSets[nextIndex];
    if (nextSet) {
      navigateToSet(nextSet.id);
    }
  };

  const handleAddNewSet = () => {
    if (!playground.canAddInputVariableSet) {
      return;
    }

    const newSetId = playground.addEmptyInputVariableSet();
    navigateToSet(newSetId);
  };

  const normalizeImportedVariables = useCallback(
    (variables: Record<string, string>) => {
      if (playground.blueprint.prompt_template.type !== "chat") {
        return variables;
      }

      const normalizedVariables = { ...variables };
      const templateMessages = playground.blueprint.prompt_template.messages;

      templateMessages.forEach((message, index) => {
        if (message.role !== "placeholder" || !("name" in message)) {
          return;
        }

        const variableValue = normalizedVariables[message.name];
        if (!variableValue) {
          return;
        }

        const importedMessages = parseImportedThreadMessages(variableValue);
        const firstImportedMessage = importedMessages?.[0];
        if (!importedMessages || firstImportedMessage?.role !== "system") {
          return;
        }

        const matchingTemplateSystemMessage = templateMessages
          .slice(0, index)
          .reverse()
          .find(
            templateMessage => templateMessage.role === "system" && "content" in templateMessage,
          );

        if (
          matchingTemplateSystemMessage &&
          "content" in matchingTemplateSystemMessage &&
          contentMatches(matchingTemplateSystemMessage.content, firstImportedMessage.content)
        ) {
          normalizedVariables[message.name] = JSON.stringify(importedMessages.slice(1), null, 2);
        }
      });

      return normalizedVariables;
    },
    [playground.blueprint.prompt_template],
  );

  const handleRequestImported = useCallback(
    (
      requestId: number,
      importedVariables: Record<string, string>,
      toolData?: RequestToolData | null,
    ) => {
      if (!set) {
        return;
      }

      // Merge tool/function data from the request into the current
      // template, using the same shared logic as "open in playground".
      if (toolData && playground.blueprint.prompt_template.type === "chat") {
        // RequestToolData uses null where ToolFields uses undefined; the runtime
        // behaviour is identical for our comparison logic so a cast is safe here.
        const merged = getMissingToolData(
          playground.blueprint.prompt_template,
          toolData as unknown as Parameters<typeof getMissingToolData>[1],
        );
        if (merged) {
          if (merged.functions) {
            playground.updateFunctions(merged.functions);
          }
          if (merged.function_call) {
            playground.updateFunctionCall(merged.function_call);
          }
          if (merged.tools) {
            playground.updateTools(merged.tools);
          }
          if (merged.tool_choice) {
            playground.updateToolChoice(merged.tool_choice);
          }
        }
      }

      // If not in chat mode, switch automatically so the diff-based
      // chat history can be computed and displayed.
      if (!playground.isSessionModeChat) {
        playground.toggleSessionMode();
      }

      const variableSetIndex = playground.input_variable_sets.findIndex(s => s.id === set.id);
      if (variableSetIndex < 0) {
        return;
      }

      // Use the imported variables passed directly from the RequestImport
      // callback rather than reading from the playground store, which may
      // not have been updated yet due to asynchronous MobX reactions.
      resetChatMutation.mutate({
        workspaceId: playground.workspace_id,
        sessionId: playground.instanceId,
        request: {
          request_log_id: requestId,
          variable_set_index: variableSetIndex,
          input_variables: importedVariables,
        },
      });
    },
    [set, playground, resetChatMutation],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        ref={setDialogContentElement}
        className="flex h-[620px] max-h-[82vh] max-w-4xl flex-col gap-0 overflow-visible p-0"
      >
        <DialogHeader className="border-b border-gray-100 px-5 py-3">
          <div className="flex items-center justify-between gap-4 pr-8">
            <div className="flex min-w-0 items-center gap-2">
              <DialogTitle className="sr-only">Edit {setName}</DialogTitle>
              {set ? (
                <Select value={set.id} onValueChange={navigateToSet}>
                  <SelectTrigger className="-mx-1.5 h-8 w-auto min-w-0 max-w-[320px] justify-start gap-1 rounded-md border-0 bg-transparent px-1.5 py-0 text-base font-semibold text-gray-900 shadow-none ring-0 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:ring-0 data-[state=open]:bg-gray-50 data-[state=open]:text-gray-900 [&>svg]:ml-1 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0">
                    <SelectValue placeholder={setName} />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg border-gray-200 p-1 shadow-lg">
                    {variableSets.map(variableSet => (
                      <SelectItem
                        key={variableSet.id}
                        value={variableSet.id}
                        className="rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <span className="truncate">{variableSet.name || "Untitled set"}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="truncate text-base font-semibold text-gray-900">{setName}</div>
              )}
              {canNavigateSets && (
                <div className="flex shrink-0 items-center gap-0.5 rounded-md bg-gray-50 p-0.5 text-gray-500">
                  <Button
                    type="button"
                    variant="ghost"
                    size="tinyIcon"
                    className="h-6 w-6 hover:bg-white hover:text-gray-900 disabled:hover:bg-transparent"
                    disabled={activeSetIndex <= 0}
                    onClick={() => navigateByOffset(-1)}
                    aria-label="Previous input set"
                  >
                    <ChevronLeftIcon className="h-3.5 w-3.5" />
                  </Button>
                  <span className="min-w-8 text-center text-xs tabular-nums">
                    {activeSetIndex + 1}/{variableSets.length}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="tinyIcon"
                    className="h-6 w-6 hover:bg-white hover:text-gray-900 disabled:hover:bg-transparent"
                    disabled={activeSetIndex >= variableSets.length - 1}
                    onClick={() => navigateByOffset(1)}
                    aria-label="Next input set"
                  >
                    <ChevronRightIcon className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
              <BasicTooltip
                content="You can keep up to 5 input sets"
                shouldShow={!playground.canAddInputVariableSet}
              >
                <span className="flex h-fit w-fit items-center justify-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1.5 px-2 text-xs font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                    onClick={handleAddNewSet}
                    disabled={!playground.canAddInputVariableSet}
                  >
                    <PlusIcon className="h-3.5 w-3.5" />
                    New
                  </Button>
                </span>
              </BasicTooltip>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <BasicTooltip
                content={
                  isEvaluateMode
                    ? "Blank values use the mapped sheet default for this input set."
                    : `${filledVariableCount} of ${detectedVariableCount} input variables filled.`
                }
              >
                <span className="rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium tabular-nums text-gray-600 ring-1 ring-gray-200/70">
                  {setStatusText}
                </span>
              </BasicTooltip>
              <Select
                value={playground.template_format}
                onValueChange={value => playground.setTemplateFormat(value as TemplateFormat)}
              >
                <SelectTrigger className="h-7 w-auto rounded-full border-gray-200/70 bg-gray-50 px-2.5 py-0 text-xs font-medium text-gray-600 shadow-none ring-0 hover:border-gray-300 hover:bg-gray-100 hover:text-gray-900 focus:ring-1 focus:ring-gray-200 data-[state=open]:border-gray-300 data-[state=open]:bg-gray-100 [&>svg]:ml-1 [&>svg]:h-3.5 [&>svg]:w-3.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-lg border-gray-200 p-1 shadow-lg">
                  {templateFormatOptions.map(format => (
                    <SelectItem
                      key={format}
                      value={format}
                      className="rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {format}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </DialogHeader>
        {set && (
          <div className="min-h-0 flex-1 overflow-hidden px-1">
            <InputVariableConfigurator
              key={set.id}
              className="h-full"
              inputVariables={set?.variables || []}
              initialName={set?.name || ""}
              onChange={handleVariablesChange}
              onClose={onClose}
              enableAIDataset
              promptTemplate={
                playground.blueprint.prompt_template
                  ? JSON.stringify(playground.blueprint.prompt_template, null, 2)
                  : ""
              }
              onAddVariableSet={playground.addInputVariableSet}
              currentVariableSetCount={playground.input_variable_sets.length}
              onSetNameImported={name => {
                if (set?.id) {
                  playground.updateInputVariableSetName(set.id, name);
                }
              }}
              onRequestImported={handleRequestImported}
              normalizeImportedVariables={normalizeImportedVariables}
              popoverPortalContainer={dialogContentElement}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
});

export default EditTestCaseDialog;
